import { useModalFocus } from '../lib/useModalFocus.js';

export interface UndecidedRequirement {
  id: string;
  title: string;
}

export interface SceneWithoutRequirement {
  id: string;
  sceneNumber: string | null;
  slugline: string | null;
}

/**
 * GOING ON TO LOCATIONS WITH THE BREAKDOWN STILL OPEN.
 *
 * Step 3 used to be a wall: Continue stayed disabled until every proposal was
 * decided and every scene had a requirement. The owner's review of 2026-10-27
 * asked to be able to go on anyway, with a warning that says what is left
 * (decision D1). This is that warning.
 *
 * IT NAMES THE ROWS, NOT A COUNT. "7 unconfirmed" invites the wrong conclusion
 * — that they will be handled, or were discarded. The list says which, and the
 * sentence above each list says the one consequence that matters: nothing is
 * searched for them. Matching takes only a confirmed requirement
 * (`isMatchable`), so an undecided one is not lost and not decided — it waits,
 * and can be confirmed from the Locations tab or back here.
 *
 * NOTHING HERE CHANGES A REQUIREMENT. "Continue anyway" records that the
 * producer moved on (`setup_breakdown_continued_at` and an activity line with
 * the counts) and nothing else. Dismissing the undecided ones on the way out
 * was the rejected alternative: it turns "not decided" into "decided no".
 */
export function ContinueWithOpenBreakdownDialog({
  undecided,
  scenesWithoutRequirement,
  approvedCount,
  busy = false,
  error = null,
  onStay,
  onContinue,
}: {
  undecided: readonly UndecidedRequirement[];
  scenesWithoutRequirement: readonly SceneWithoutRequirement[];
  /** Confirmed requirements — what WILL be searched for. */
  approvedCount: number;
  busy?: boolean;
  error?: string | null;
  onStay: () => void;
  onContinue: () => void;
}) {
  const modal = useModalFocus<HTMLDivElement>(() => {
    if (!busy) onStay();
  });

  const listStyle = {
    margin: '8px 0 0',
    padding: '0 0 0 18px',
    maxHeight: 180,
    overflowY: 'auto' as const,
    fontSize: 13,
    lineHeight: 1.6,
  };

  return (
    <div
      data-testid="continue-anyway-dialog"
      onKeyDown={modal.onKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="continue-anyway-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(35, 35, 35, 0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '5vh 16px',
        overflowY: 'auto',
        zIndex: 40,
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onStay();
      }}
    >
      <div
        ref={modal.ref}
        style={{
          background: 'var(--color-bg)',
          width: 'min(600px, 100%)',
          border: '1px solid var(--color-divider)',
          boxShadow: '0 18px 48px rgba(16, 24, 40, 0.18)',
        }}
      >
        <header style={{ padding: '18px 22px', borderBottom: '2px solid var(--ink)' }}>
          <h2
            id="continue-anyway-title"
            style={{ margin: 0, fontFamily: 'var(--font-heading)', fontSize: 22, letterSpacing: '0.02em' }}
          >
            GO ON WITH THE BREAKDOWN OPEN?
          </h2>
          <p className="mm" style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.6 }}>
            You can explore locations now and finish these later — here, or on the Locations tab.
            Nothing below is removed or decided for you.
          </p>
        </header>

        <div style={{ padding: '18px 22px', display: 'grid', gap: 18 }}>
          {undecided.length === 0 ? null : (
            <section data-testid="continue-anyway-undecided">
              <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700 }}>
                {`${String(undecided.length)} requirement${undecided.length === 1 ? '' : 's'} still need${undecided.length === 1 ? 's' : ''} a decision — ${undecided.length === 1 ? 'it' : 'they'} will not be searched for`}
              </p>
              <ul style={listStyle}>
                {undecided.map((r) => (
                  <li key={r.id} data-testid={`continue-anyway-undecided-${r.id}`}>
                    {r.title}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {scenesWithoutRequirement.length === 0 ? null : (
            <section data-testid="continue-anyway-uncovered">
              <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700 }}>
                {`${String(scenesWithoutRequirement.length)} scene${scenesWithoutRequirement.length === 1 ? ' has' : 's have'} no location requirement — nothing will be searched for ${scenesWithoutRequirement.length === 1 ? 'it' : 'them'}`}
              </p>
              <ul style={listStyle}>
                {scenesWithoutRequirement.map((s) => (
                  <li key={s.id} data-testid={`continue-anyway-uncovered-${s.id}`}>
                    <strong>{`Sc ${s.sceneNumber ?? '—'}`}</strong>{' '}
                    <span className="mm">{s.slugline ?? 'Untitled scene'}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <p
            data-testid="continue-anyway-searched"
            style={{
              margin: 0,
              padding: '12px 14px',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-divider)',
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            {approvedCount === 0
              ? 'No requirement is confirmed yet, so there is nothing to search for until you confirm one.'
              : `${String(approvedCount)} confirmed requirement${approvedCount === 1 ? '' : 's'} will be searched for.`}
          </p>

          {error === null ? null : (
            <p data-testid="continue-anyway-error" role="alert" style={{ margin: 0, fontSize: 13, color: 'var(--danger)' }}>
              {error}
            </p>
          )}
        </div>

        <footer
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 12,
            flexWrap: 'wrap',
            padding: '14px 22px',
            borderTop: '1px solid var(--color-divider)',
            background: 'var(--color-surface)',
          }}
        >
          <button type="button" className="btn btn-ghost" data-testid="continue-anyway-stay" disabled={busy} onClick={onStay}>
            Stay and review
          </button>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="continue-anyway-confirm"
            disabled={busy}
            onClick={onContinue}
          >
            {busy ? 'Continuing…' : 'Continue anyway'}
          </button>
        </footer>
      </div>
    </div>
  );
}
